/**
 * Auth API client.
 * Flow: Auth Admin login → Verification Admin request → Auth Admin login/verify.
 * Headers: x-client-id only (same as verification-admin).
 */

import { getClientId } from './clientId';
import { buildHmacHeaders } from './signing';
import {
  API_ENV_TEST,
  getSelectedEnvironment,
} from '../utils/apiEnvironment';

const COMMUNITY_HMAC_SECRET = process.env.REACT_APP_COMMUNITY_HMAC_SECRET || '';

const AUTH_ADMIN_PROD_BASE =
  process.env.REACT_APP_AUTH_ADMIN_PROD_URL ||
  process.env.REACT_APP_AUTH_ADMIN_API_URL ||
  'https://auth-admin.fancian.net';

const AUTH_ADMIN_TEST_BASE =
  process.env.REACT_APP_AUTH_ADMIN_TEST_URL || 'https://test-auth-admin.fancian.net';

const APP_CONFIG_ADMIN_PROD_BASE =
  process.env.REACT_APP_CONFIG_ADMIN_PROD_URL ||
  process.env.REACT_APP_CONFIG_ADMIN_API_URL ||
  'https://app-config-admin.fancian.net';

const APP_CONFIG_ADMIN_TEST_BASE =
  process.env.REACT_APP_CONFIG_ADMIN_TEST_URL || 'https://test-app-config-admin.fancian.net';

const COMMUNITY_ADMIN_PROD_BASE =
  process.env.REACT_APP_COMMUNITY_ADMIN_PROD_URL ||
  process.env.REACT_APP_COMMUNITY_ADMIN_API_URL ||
  'https://community-admin.fancian.net';

const COMMUNITY_ADMIN_TEST_BASE =
  process.env.REACT_APP_COMMUNITY_ADMIN_TEST_URL || 'https://test-community-admin.fancian.net';

const PUSH_ADMIN_PROD_BASE =
  process.env.REACT_APP_PUSH_ADMIN_PROD_URL ||
  process.env.REACT_APP_PUSH_ADMIN_API_URL ||
  'https://push-admin.fancian.net';

const PUSH_ADMIN_TEST_BASE =
  process.env.REACT_APP_PUSH_ADMIN_TEST_URL || 'https://test-push-admin.fancian.net';

const GAME_ADMIN_PROD_BASE =
  process.env.REACT_APP_GAME_ADMIN_PROD_URL ||
  process.env.REACT_APP_GAME_ADMIN_API_URL ||
  'https://game-admin.fancian.net';

const GAME_ADMIN_TEST_BASE =
  process.env.REACT_APP_GAME_ADMIN_TEST_URL || 'https://test-game-admin.fancian.net';

const GA_ADMIN_PROD_BASE =
  process.env.REACT_APP_GA_ADMIN_PROD_URL ||
  process.env.REACT_APP_GA_ADMIN_API_URL ||
  '';

const GA_ADMIN_TEST_BASE =
  process.env.REACT_APP_GA_ADMIN_TEST_URL || 'https://test-ga-admin.fancian.net';

const VERIFICATION_ADMIN_BASE =
  process.env.REACT_APP_VERIFICATION_ADMIN_PROD_URL ||
  process.env.REACT_APP_VERIFICATION_ADMIN_API_URL || 'https://verification-admin.fancian.net';

const VERIFICATION_ADMIN_TEST_BASE =
  process.env.REACT_APP_VERIFICATION_ADMIN_TEST_URL || 'https://test-verification-admin.fancian.net';

function getVerificationAdminBase() {
  return getSelectedEnvironment() === API_ENV_TEST
    ? VERIFICATION_ADMIN_TEST_BASE
    : VERIFICATION_ADMIN_BASE;
}

function getAuthUserDataBase() {
  return getSelectedEnvironment() === API_ENV_TEST
    ? AUTH_ADMIN_TEST_BASE
    : AUTH_ADMIN_PROD_BASE;
}

export function getAppConfigBase() {
  return getSelectedEnvironment() === API_ENV_TEST
    ? APP_CONFIG_ADMIN_TEST_BASE
    : APP_CONFIG_ADMIN_PROD_BASE;
}

export function getCommunityAdminBase() {
  return getSelectedEnvironment() === API_ENV_TEST
    ? COMMUNITY_ADMIN_TEST_BASE
    : COMMUNITY_ADMIN_PROD_BASE;
}

function getPushAdminBase() {
  return getSelectedEnvironment() === API_ENV_TEST
    ? PUSH_ADMIN_TEST_BASE
    : PUSH_ADMIN_PROD_BASE;
}

function getGameAdminBase() {
  return getSelectedEnvironment() === API_ENV_TEST
    ? GAME_ADMIN_TEST_BASE
    : GAME_ADMIN_PROD_BASE;
}

export function getGaAdminBase() {
  const base =
    getSelectedEnvironment() === API_ENV_TEST ? GA_ADMIN_TEST_BASE : GA_ADMIN_PROD_BASE;
  if (base) return base.replace(/\/$/, '');
  return '/api-ga-admin';
}

function request(baseUrl, path, options = {}) {
  const base = (baseUrl || '').replace(/\/$/, '');
  const url = base ? `${base}${path}` : path;
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-client-id': getClientId(),
      ...options.headers,
    },
  }).catch((error) => {
    // Tag transport failures without logging credentials, payloads or responses.
    if (path === '/api-admin/v0/auth/login') error.authStage = 'admin-login';
    else if (path === '/api-admin/v0/auth/login/verify') error.authStage = 'admin-verify';
    else if (path === '/api/v0/verification/request') error.authStage = 'email-verification';
    throw error;
  });
}

/**
 * Reads the current user's access token from localStorage.
 * Falls back to null if not found or on parse error.
 */
export function getAuthToken() {
  try {
    const stored = window.localStorage.getItem('fancian-admin-auth');
    return stored ? (JSON.parse(stored)?.token ?? null) : null;
  } catch {
    return null;
  }
}

export async function parseResponse(res, path) {
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON response
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('[auth API]', path, res.status, /\/auth\/login(?:\/verify)?$|\/verification\/(?:request|code)$/.test(path) ? '[authentication data redacted]' : /\/suspension$/.test(path) ? '[restriction data redacted]' : data ?? text);
  }

  if (!res.ok) {
    const err = new Error(
      data?.msg || data?.message || data?.error || `Request failed (${res.status})`
    );
    err.status = res.status;
    err.data = data;
    if (data?.data?.wait_time != null) {
      err.waitTime = data.data.wait_time;
    }
    if (data?.data?.retry != null) {
      err.retry = data.data.retry;
    }
    throw err;
  }
  return data;
}

/**
 * Parses responses from admin-config-api-serverless.
 * Success shape: { data: ... }
 * Error shape:   { code: number, msg: string }
 */
async function parseAppConfigResponse(res, path) {
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON
  }

  if (process.env.NODE_ENV !== 'production') {
    const privateReview = /[?&]resource=review_export(?:&|$)/.test(path);
    console.log('[app-config API]', path, res.status, privateReview ? '[private review data redacted]' : body ?? text);
  }

  if (!res.ok) {
    const err = new Error(body?.msg || body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = body?.code;
    err.data = body;
    throw err;
  }

  return body?.data ?? body;
}

/**
 * Parses responses from admin-community-api-serverless.
 * Success: { data, meta? } or raw data
 * Error: { code, msg }
 */
async function parseCommunityAdminResponse(res, path) {
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('[community-admin API]', path, res.status, body ?? text);
  }

  if (!res.ok) {
    const err = new Error(body?.msg || body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = body?.code;
    err.data = body;
    throw err;
  }

  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) {
    return body;
  }
  return { data: body };
}

/**
 * Makes an authenticated request to admin-community-api-serverless.
 */
export async function requestCommunityAdmin(path, options = {}) {
  const token = getAuthToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), { status: 401 });
  }
  if (!COMMUNITY_HMAC_SECRET) {
    throw Object.assign(
      new Error(
        'Missing REACT_APP_COMMUNITY_HMAC_SECRET. The community API requires an HMAC signature; ' +
          'set this env var (TEST secret only) and restart the dev server.',
      ),
      { status: 0 },
    );
  }
  const base = getCommunityAdminBase().replace(/\/$/, '');
  const url = `${base}${path}`;
  const hmacHeaders = await buildHmacHeaders(COMMUNITY_HMAC_SECRET, getClientId());
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...hmacHeaders,
      ...options.headers,
    },
  });
  // Always return the parsed envelope ({ data, meta? }). Callers in
  // communityAdmin.js pick `.data` (single) or the full envelope (lists).
  return parseCommunityAdminResponse(res, path);
}

/**
 * Makes an authenticated request to admin-config-api-serverless.
 * Reads the JWT from localStorage automatically.
 * Uses selected prod/test app-config base by environment toggle.
 * @param {string} path - API path (e.g. /app-versions)
 * @param {RequestInit} options - fetch options
 * @returns {Promise<any>} - resolved data payload
 */
export async function requestAppConfigAdmin(path, options = {}) {
  const { expectedEnvironment, ...requestOptions } = options;
  if (expectedEnvironment && expectedEnvironment !== getSelectedEnvironment()) {
    throw Object.assign(new Error('API 환경이 변경됐습니다. 대상 기록을 다시 열어주세요.'), { status: 409 });
  }
  const token = getAuthToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), { status: 401 });
  }
  const base = getAppConfigBase().replace(/\/$/, '');
  const url = `${base}${path}`;
  const res = await fetch(url, {
    ...requestOptions,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...requestOptions.headers,
    },
  });
  return parseAppConfigResponse(res, path);
}

/**
 * Auth-admin GET request with Authorization: Bearer <accessToken>.
 * Use for get-* APIs that require Login Verify token.
 * @param {string} path - Path (e.g. /api-admin/v0/users)
 * @param {RequestInit} options - fetch options
 * @param {string} accessToken - JWT from login/verify
 * @returns {Promise<Response>}
 */
export function requestAuthAdmin(path, options = {}, accessToken) {
  if (!accessToken) {
    return Promise.reject(new Error('Access token is required for auth-admin get-* calls'));
  }
  const base = (getAuthUserDataBase() || '').replace(/\/$/, '');
  const url = base ? `${base}${path}` : path;
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-client-id': getClientId(),
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  });
}

/**
 * Push-admin request with Authorization: Bearer <accessToken>.
 * Uses selected prod/test push-admin base by environment toggle.
 * @param {string} path - Path (e.g. /api-admin/v0/push/test)
 * @param {RequestInit} options - fetch options
 * @param {string} accessToken - JWT from login/verify
 * @returns {Promise<Response>}
 */
export function requestPushAdmin(path, options = {}, accessToken) {
  if (!accessToken) {
    return Promise.reject(new Error('Access token is required for push-admin calls'));
  }
  const base = (getPushAdminBase() || '').replace(/\/$/, '');
  const url = base ? `${base}${path}` : path;
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-client-id': getClientId(),
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  });
}

/**
 * Game-admin request with Authorization: Bearer <accessToken>.
 * Uses selected prod/test game-admin base (game-admin.fancian.net / test-game-admin.fancian.net).
 * @param {string} path - Path (e.g. /api-admin/v0/game/rps/config)
 * @param {RequestInit} options - fetch options
 * @param {string} accessToken - JWT from auth-admin login/verify
 * @returns {Promise<Response>}
 */
export function requestGameAdmin(path, options = {}, accessToken) {
  if (!accessToken) {
    return Promise.reject(new Error('Access token is required for game-admin calls'));
  }
  const base = (getGameAdminBase() || '').replace(/\/$/, '');
  const url = base ? `${base}${path}` : path;
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-client-id': getClientId(),
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  });
}

/**
 * GA-admin request with Authorization: Bearer <accessToken>.
 * Uses selected prod/test ga-admin base by environment toggle.
 * Falls back to same-origin /api-ga-admin when env URLs are unset.
 */
export function requestGaAdmin(path, options = {}, accessToken) {
  if (!accessToken) {
    return Promise.reject(new Error('Access token is required for ga-admin calls'));
  }
  const base = getGaAdminBase();
  const url = `${base}${path}`;
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-client-id': getClientId(),
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  });
}

/**
 * Step 1: Auth Admin login → Verification Admin request.
 * 1) POST auth-admin /api-admin/v0/auth/login (id, password) → transaction_id
 * 2) POST verification-admin /api/v0/verification/request → verification_id (data.id)
 * @returns {Promise<{ requires2fa: true, tempToken: number, transactionId: string, email: string }>}
 */
export async function login(email, password) {
  if (!email || !password) {
    throw new Error('Email and password are required');
  }
  const trimmedEmail = typeof email === 'string' ? email.trim() : '';
  if (!trimmedEmail) {
    throw new Error('Email is required');
  }

  try {
    // Admin identity is shared. TEST/PROD selects data APIs, not login or 2FA.
    const loginRes = await request(AUTH_ADMIN_PROD_BASE, '/api-admin/v0/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        id: trimmedEmail,
        password: password.trim(),
      }),
    });
    const loginBody = await parseResponse(loginRes, '/api-admin/v0/auth/login');
    const transactionId = loginBody?.data?.transaction_id;
    if (transactionId == null || transactionId === '') {
      throw new Error('Invalid response: missing transaction_id');
    }

    // 2) Verification Admin request (send email with code)
    const verifRes = await request(
      VERIFICATION_ADMIN_BASE,
      '/api/v0/verification/request',
      {
        method: 'POST',
        body: JSON.stringify({
          type: 1,
          user_data: trimmedEmail,
          method: 1,
        }),
      }
    );
    const verifBody = await parseResponse(
      verifRes,
      '/api/v0/verification/request'
    );
    const verificationId = verifBody?.data?.id;
    if (verificationId == null) {
      throw new Error('Invalid response: missing verification id');
    }

    return {
      requires2fa: true,
      tempToken: verificationId,
      transactionId,
      email: trimmedEmail,
    };
  } catch (err) {
    if (err.status === 406 && err.waitTime != null) {
      const e = new Error('WAIT_REQUIRED');
      e.waitTime = err.waitTime;
      throw e;
    }
    if (err.status === 429) {
      throw new Error('TOO_MANY_REQUESTS');
    }
    throw err;
  }
}

/**
 * Step 2: verify 6-digit code via Auth Admin login/verify.
 * POST /api-admin/v0/auth/login/verify (verification_id, code, transaction_id)
 * @returns {Promise<{ user: { id, email, role, displayName }, accessToken: string }>}
 */
export async function verify2FA(email, code, tempToken, transactionId) {
  const normalized = String(code).replace(/\s/g, '');
  if (normalized.length !== 6 || !/^\d{6}$/.test(normalized)) {
    throw new Error('Please enter a valid 6-digit code');
  }
  if (tempToken == null) {
    throw new Error('Session expired. Please start over.');
  }
  if (transactionId == null || transactionId === '') {
    throw new Error('Session expired. Please start over.');
  }

  const res = await request(
    AUTH_ADMIN_PROD_BASE,
    '/api-admin/v0/auth/login/verify',
    {
      method: 'POST',
      body: JSON.stringify({
        verification_id: Number(tempToken),
        code: normalized,
        transaction_id: transactionId,
      }),
    }
  );
  const body = await parseResponse(res, '/api-admin/v0/auth/login/verify');

  const userId = body?.data?.user_id;
  const accessToken = body?.data?.access_token;
  if (userId == null || !accessToken) {
    throw new Error('Invalid response: missing user or token');
  }

  const role = String(body?.data?.role || 'USER').toLowerCase();
  const username = String(body?.data?.username || '').trim();
  const displayEmail = email && typeof email === 'string' ? email.trim() : '';
  return {
    user: {
      id: String(userId),
      email: displayEmail || 'user@example.com',
      role,
      username: username || (displayEmail ? displayEmail.split('@')[0] : ''),
      displayName: username || (displayEmail ? displayEmail.split('@')[0] : 'User'),
      mustChangePassword: Boolean(body?.data?.must_change_password),
    },
    accessToken,
  };
}

export async function requestPasswordChangeCode(email) {
  const res = await request(getVerificationAdminBase(), '/api/v0/verification/request', {
    method: 'POST',
    body: JSON.stringify({ type: 1, user_data: String(email || '').trim(), method: 1 }),
  });
  const body = await parseResponse(res, '/api/v0/verification/request');
  const id = body?.data?.id;
  if (id == null) throw new Error('Invalid response: missing verification id');
  return Number(id);
}

export async function changePassword({ verificationId, code, newPassword }) {
  const token = getAuthToken();
  const res = await request(getAuthUserDataBase(), '/api-admin/v0/admin-account/password/change', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ verification_id: verificationId, code, new_password: newPassword }),
  });
  return parseResponse(res, '/api-admin/v0/admin-account/password/change');
}
